import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, SearchX } from "@/lib/public-icons";
import { useState } from "react";
import { formatDateOnly } from "@alm/domain";
import { AcLogo } from "@/components/marketing/ac-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";

export const Route = createFileRoute("/verify-certificate")({
  component: VerifyCertificate,
  head: () => ({
    meta: [
      { title: "Verify a training certificate" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

/** Public certificate check, open on purpose: a state surveyor or employer holds only the paper. It
 * confirms the certificate exists and who holds it, and nothing else about the person. */
function VerifyCertificate() {
  const [typed, setTyped] = useState("");
  const [asked, setAsked] = useState("");
  const [empty, setEmpty] = useState(false);

  const result = useQuery({
    ...orpcPublic.workforce.training.verifyCertificate.queryOptions({
      input: { certificateNumber: asked },
    }),
    enabled: asked.length > 0,
    retry: false,
  });
  /* Only a number that matches nothing is "no certificate"; a refused or
     failed request must not tell a surveyor that a real certificate is fake. */
  const code = (result.error as { code?: string } | null)?.code;
  const unknown = code === "NOT_FOUND" || code === "BAD_REQUEST";

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col justify-center gap-6 px-4 py-12">
      {/* Whoever checks a certificate (a surveyor, the next employer) should
          see whose record they are reading before they read it. */}
      <AcLogo className="justify-center" />
      <Card className="shadow-halo">
        <CardContent className="space-y-5 p-6">
          <div className="space-y-1.5 text-center">
            <h1 className="font-greeting text-2xl text-balance">Verify a training certificate</h1>
            <p className="text-muted-foreground text-sm">
              Type the number printed on the certificate.
            </p>
          </div>
          <div className="space-y-2">
            <form
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                setEmpty(!typed.trim());
                setAsked(typed.trim());
              }}
            >
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="certificate-number">Certificate number</Label>
                <Input
                  id="certificate-number"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  placeholder="CERT-000123-A1B2C3D4E5"
                  autoCapitalize="characters"
                  spellCheck={false}
                  aria-invalid={empty ? true : undefined}
                  aria-describedby={empty ? "certificate-empty" : undefined}
                />
              </div>
              <Button type="submit">Verify certificate</Button>
            </form>
            <p
              id="certificate-empty"
              role="alert"
              className="text-destructive text-sm empty:hidden"
            >
              {empty ? "Type the number printed on the certificate." : null}
            </p>
          </div>
        </CardContent>
      </Card>

      <div aria-live="polite">
        {asked.length === 0 ? null : result.isPending ? (
          <Skeleton className="h-28 w-full" />
        ) : result.isError && !unknown ? (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <p className="text-sm">
                {code === "TOO_MANY_REQUESTS"
                  ? "Too many checks from this connection. Wait a minute, then try again."
                  : "Couldn't check the number. Check your connection and try again."}
              </p>
              <Button variant="outline" size="sm" onClick={() => void result.refetch()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : result.isError ? (
          <Card>
            <CardContent className="flex items-start gap-3 p-4">
              <SearchX className="text-muted-foreground mt-0.5 size-5 shrink-0" />
              <div>
                <p className="font-medium">No certificate with that number</p>
                <p className="text-muted-foreground text-sm">
                  Check the number and try again. Certificates that have not been issued yet do not
                  appear here.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex items-start gap-3 p-4">
              <BadgeCheck className="text-ok mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <div className="space-y-2">
                <p className="text-ok font-medium">Valid certificate</p>
                <dl className="space-y-1 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Held by</dt>
                    <dd className="text-base font-medium">{result.data.holder || "-"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Course</dt>
                    <dd className="font-medium">{result.data.course}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Issued</dt>
                    <dd>{formatDateOnly(result.data.issuedAt)}</dd>
                  </div>
                  {result.data.score === null ? null : (
                    <div>
                      <dt className="text-muted-foreground">Score</dt>
                      <dd className="tabular-nums">{result.data.score}%</dd>
                    </div>
                  )}
                </dl>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
}
